import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import ActivateCardModal from '../ActivateCardModal';
import CardDetailsPanel from '../CardDetailsPanel';
import SetNewPinModal from '../SetNewPinModal';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import {alertActions} from '../../../../state/actions/alert.actions';
import {bankingActions} from '../../../../state/actions/banking.actions';
import {cardActions} from '../../../../state/actions/card.actions';
import {
  cardsHelpers,
  scaHelpers,
  systemHelpers,
} from '../../../../utils/helpers';
import {
  DEFAULT_MODAL_PROPS,
  SCAActionsConstants,
  subscriptionFormFields,
} from '../../../../constants';
import {firebaseEvents} from '../../../../snippets/firebase';


const {
  cardLimitFieldName,
  cardLimitPeriodFieldName,
  limitPeriodShortFieldName
} = subscriptionFormFields;

const cardFields = [cardLimitFieldName, limitPeriodShortFieldName];

const {
  CARD_ACTIVATE_ACTION,
  CARD_EDITION_ACTION,
  CARD_SET_NEW_PIN_ACTION,
  CARD_UNBLOCK_PIN_ACTION,
} = SCAActionsConstants;

const {getAuthHeaders} = scaHelpers;

const {logEvent} = systemHelpers;

const CardDetailsSection = ({
  activateCard,
  setCardPin,
  unblockCardPin,
  updateCard,
  updateCardLimits,

  cardDetails = { bankingData: null, mainData: null },
  edit = false,
  onUpdateCardDetails,
  pageLoading = { exists: true, loading: true },
  subscription,
  ...rest
}) => {
  const uriParams = useParams();
  const [t] = useTranslation(['main', 'subscriptions', 'cards']);
  const [authWindowProps, setAuthWindowProps] = useState(DEFAULT_MODAL_PROPS);
  const [activeAuthAction, setActiveAuthAction] = useState(null);
  const [activateModalProps, setActivateModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [newPinModalProps, setNewPinModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [tempData, setTempData] = useState({
    callbackFunction: undefined,
    errorCallbackFunction: undefined,
    successCallbackFunction: undefined,
    data: undefined
  });

  const {cardId, userId} = useMemo(() => ({
    cardId: uriParams?.cardId,
    userId: uriParams?.userId
  }), [uriParams]);

  const cardDetailsPanel = useMemo(() => ({
    bankingData: cardDetails.bankingData,
    cardData: cardDetails.mainData,
    loading: pageLoading.loading
  }), [pageLoading, cardDetails]);

  const authModalProps = useMemo(() => scaHelpers.getAuthModalProps({action: activeAuthAction, t}), [activeAuthAction, t]);

  const SCAErrorCallback = ({response, successCallback, errorCallback}) => {
    scaHelpers.SCAResponseCallback({
      response,
      scaCallback: (scaProps) => {
        setAuthWindowProps({...authWindowProps, ...scaProps});
        successCallback && successCallback();
      },
      errorCallback
    });
  }

  const handleCloseAuthModal = () => {
    const callErrorCallback = () => tempData?.errorCallbackFunction?.();

    const operationActions = {
      [CARD_ACTIVATE_ACTION]: () => setActivateModalProps({...activateModalProps, loading: false}),
      [CARD_EDITION_ACTION]: callErrorCallback,
      [CARD_SET_NEW_PIN_ACTION]: () => setNewPinModalProps(({...newPinModalProps, loading: false})),
      [CARD_UNBLOCK_PIN_ACTION]: callErrorCallback
    }

    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.();
  }

  const handleSuccessAuth = () => {
    const operationActions = {
      [CARD_ACTIVATE_ACTION]: handleActivateCard,
      [CARD_EDITION_ACTION]: handleEditCard,
      [CARD_SET_NEW_PIN_ACTION]: handleSetCardPin,
      [CARD_UNBLOCK_PIN_ACTION]: handleUnblockCard,
    }

    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.();
  }

  const resolveActionParams = ({ data, errorCallback, successCallback } = {}, fallback = tempData) => {
    return {
      data: data || fallback.data,
      errorCallback: errorCallback || fallback.errorCallbackFunction,
      successCallback: successCallback || fallback.successCallbackFunction,
    };
  };

  const handleAuthAction = ({ data, errorCallback, successCallback }, actionType, actionHandler) => {
    setTempData({
      ...tempData,
      data,
      errorCallbackFunction: errorCallback,
      successCallbackFunction: successCallback,
    });

    setActiveAuthAction(actionType);
    actionHandler({ data, errorCallback, successCallback });
  }

  const handleOkActivateModal = (params) => {
    handleAuthAction(params, CARD_ACTIVATE_ACTION, handleActivateCard);
  }

  const handleOkNewPinModal = (params) => {
    handleAuthAction(params, CARD_SET_NEW_PIN_ACTION, handleSetCardPin);
  }

  const onFormSubmit = (params) => {
    handleAuthAction(params, CARD_EDITION_ACTION, handleEditCard);
  }

  const handleUnblockClick = (params) => {
    handleAuthAction(params, CARD_UNBLOCK_PIN_ACTION, handleUnblockCard);
  }

  const handleActivateCard = (params = {}) => {
    const {data, successCallback, errorCallback} = resolveActionParams(params);

    setActivateModalProps({...activateModalProps, loading: true});

    activateCard({
      cardId,
      userId,
      data,
      headers: getAuthHeaders(CARD_ACTIVATE_ACTION),
      successCallback: (response) => {
        handleCloseActivateModal();

        onUpdateCardDetails?.({
          ...cardDetails,
          bankingData: response,
        });
        successCallback?.(response);
        logEvent(firebaseEvents.CARDS_ACTIVATE_CARD);
      },
      errorCallback: (response) => {
        SCAErrorCallback({
          response,
          errorCallback: () => {
            setActivateModalProps({...activateModalProps, open: true, loading: false});
            errorCallback?.(response);
          }
        });
      }
    })
  }

  const handleOpenActivateModal = () => setActivateModalProps({...activateModalProps, open: true});

  const handleCloseActivateModal = () => setActivateModalProps({...activateModalProps, loading: false, open: false});

  const handleOpenNewPinModal = () => setNewPinModalProps({...newPinModalProps, open: true});

  const handleCloseNewPinModal = () => setNewPinModalProps({...newPinModalProps, loading: false, open: false});

  const handleEditCard = (params = {}) => {
    const {data, successCallback, errorCallback} = resolveActionParams(params);

    // check if need to change card limits
    let isRequiredUpdateCardLimits = false;
    cardFields.forEach(field => {
      if (cardDetails?.mainData[field] !== data[field]) isRequiredUpdateCardLimits = true;
    });

    // update card details
    const updateMainCardData = (cardBankingData) => {
      updateCard({
        data,
        id: cardId,
        headers: getAuthHeaders(CARD_EDITION_ACTION),
        successCallback: (updatedCardMainData) => {
          let updatedCardDetails = {
            ...cardDetails,
            mainData: updatedCardMainData
          };
          if (cardBankingData) updatedCardDetails = {...updatedCardDetails, bankingData: cardBankingData};

          successCallback?.();
          onUpdateCardDetails?.(updatedCardDetails);
          logEvent(firebaseEvents.CARDS_EDIT_CARD_DETAILS);
        },
        errorCallback: (response) => {
          scaHelpers.SCAResponseCallback({
            response,
            scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
            errorCallback
          });
        }
      })
    }

    if (isRequiredUpdateCardLimits) {
      const formLimitsData = cardsHelpers.getSetLimitPayload({
        ...data,
        [cardLimitPeriodFieldName]: data[limitPeriodShortFieldName]
      });
      // update card limits
      updateCardLimits({
        cardId,
        userId,
        data: formLimitsData,
        headers: getAuthHeaders(CARD_EDITION_ACTION),
        successCallback: (response) => {
          updateMainCardData(response);
          logEvent(firebaseEvents.CARDS_EDIT_CARD_LIMITS);
        },
        errorCallback: (response) => {
          SCAErrorCallback({ response, errorCallback });
        }
      });
    } else {
      updateMainCardData();
    }
  }

  const handlePauseSuccess = (data) => {
    const status = data?.lock_status;
    onUpdateCardDetails?.({
      ...cardDetails,
      bankingData: {...cardDetails.bankingData, status_code: status},
      mainData: {...cardDetails.mainData, lock_status: status}
    });
  }

  const handleUnblockCard = (params = {}) => {
    const {successCallback, errorCallback} = resolveActionParams(params);

    unblockCardPin({
      cardId,
      userId,
      headers: getAuthHeaders(CARD_UNBLOCK_PIN_ACTION),
      successCallback: (response) => {
        onUpdateCardDetails?.({
          ...cardDetails,
          bankingData: {...response, pin_try_exceeds: false},
        });
        successCallback?.(response);
        logEvent(firebaseEvents.CARDS_UNBLOCK_CARD_PIN);
      },
      errorCallback: (response) => {
        SCAErrorCallback({ response, errorCallback });
      }
    })
  }

  const handleSetCardPin = (params = {}) => {
    const {data, successCallback, errorCallback} = resolveActionParams(params);

    setNewPinModalProps({...newPinModalProps, loading: true});

    setCardPin({
      cardId,
      userId,
      data,
      headers: getAuthHeaders(CARD_SET_NEW_PIN_ACTION),
      successCallback: (response) => {
        handleCloseNewPinModal();

        onUpdateCardDetails?.({
          ...cardDetails,
          bankingData: response,
        });
        successCallback?.(response);
        logEvent(firebaseEvents.CARDS_SET_NEW_CARD_PIN);

        alertActions.success(t('cards:successPinChangedMessage'));
      },
      errorCallback: (response) => {
        SCAErrorCallback({
          response,
          errorCallback: () => {
            setNewPinModalProps({...newPinModalProps, open: true, loading: false});
            errorCallback?.(response);
          }
        });
      }
    })
  }

  return (
    <>
      <CardDetailsPanel
        cardDetails={cardDetailsPanel}
        onActivate={handleOpenActivateModal}
        onFormSubmit={onFormSubmit}
        onPauseSuccess={handlePauseSuccess}
        onSetPinClick={handleOpenNewPinModal}
        onUnblock={handleUnblockClick}
        subscription={subscription}
        {...rest}
      />

      <ActivateCardModal
        {...activateModalProps}
        onCancel={handleCloseActivateModal}
        onOk={handleOkActivateModal}
      />

      <SetNewPinModal
        {...newPinModalProps}
        onCancel={handleCloseNewPinModal}
        onOk={handleOkNewPinModal}
      />

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={authModalProps}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleSuccessAuth}
        operationName={activeAuthAction}
      />
    </>
  );
}

CardDetailsSection.propTypes = {
  cardDetails: PropTypes.shape({
    bankingData: PropTypes.object,
    mainData: PropTypes.object,
  }),
  edit: PropTypes.bool,
  onUpdateCardDetails: PropTypes.func,
  pageLoading: PropTypes.shape({
    exists: PropTypes.bool,
    loading: PropTypes.bool,
  }),
  subscription: PropTypes.object,
}

const mapDispatchToProps = {
  activateCard: bankingActions.activateCard,
  setCardPin: bankingActions.setCardPin,
  unblockCardPin: bankingActions.unblockCardPin,
  updateCard: cardActions.updateCard,
  updateCardLimits: bankingActions.updateCardLimits,
}

export default connect(null, mapDispatchToProps)(CardDetailsSection);

